import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import {
  AdvisorNotification,
  ListNotificationsRequest,
} from '@bp-core/src/lib/proto/advisor-notification.pb';
import { AdvisorNotificationGrpcServiceClient } from '@bp-core/src/lib/proto/advisor-notification.pbsc';
import { BpCoreModule } from '@bp-core/src/public-api';
import { BpNotificationRowComponent } from 'bp/modules/notifications/components/notification-row/notification-row.component';
import { AdvisorNotificationsStore } from 'bp/shared/services/advisor-notifications.store';
import { EMPTY, catchError, take } from 'rxjs';

/** Cuántas notificaciones muestra el panel; la lista completa vive en `/notificaciones`. */
const PANEL_PAGE_SIZE = 10;

/**
 * Panel desplegable de la campana: las últimas notificaciones del asesor.
 *
 * Se crea al abrir el desplegable (el `TemplatePortal` lo instancia recién ahí), así que `ngOnInit`
 * es "al abrir": consulta siempre datos frescos y no se queda con una lista vieja.
 */
@Component({
  selector: 'bp-notification-panel',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, RouterLink, BpCoreModule, BpNotificationRowComponent],
  templateUrl: './bp-notification-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpNotificationPanelComponent implements OnInit, AfterViewInit {
  private readonly client = inject(AdvisorNotificationGrpcServiceClient);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(AdvisorNotificationsStore);

  /** Pide al contenedor que cierre el desplegable (clic en una fila o en un enlace del pie). */
  readonly closed = output<void>();

  readonly items = signal<AdvisorNotification[]>([]);
  readonly isLoading = signal(true);
  readonly hasError = signal(false);
  /** Sin nada sin leer no hay nada que marcar: el botón se apaga (no basta con "hay ítems"). */
  readonly canMarkAll = computed(() => (this.store.unreadCount() ?? 0) > 0);

  private readonly panelTitle = viewChild<ElementRef<HTMLElement>>('panelTitle');

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    // El foco entra al panel por el título: desde ahí el `Tab` recorre las acciones en orden.
    this.panelTitle()?.nativeElement.focus();
  }

  /** Abre la notificación: la marca leída, cierra el panel y navega a su destino. */
  open(notification: AdvisorNotification): void {
    this.store.markAsRead(notification.id);
    this.closed.emit();

    const deepLink = notification.deepLink;
    if (deepLink && deepLink.startsWith('/')) {
      void this.router.navigateByUrl(deepLink);
    }
  }

  markAllAsRead(): void {
    // Recargar recién cuando el servidor confirmó: si se recarga antes, vuelven sin leer.
    this.store
      .markAllAsRead()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }

  private load(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.client
      .listNotifications(
        new ListNotificationsRequest({ page: 0, size: PANEL_PAGE_SIZE, unreadOnly: false }),
      )
      .pipe(
        take(1),
        catchError(() => {
          this.hasError.set(true);
          this.isLoading.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(response => {
        this.items.set(response.items ?? []);
        // La respuesta ya trae el contador: se aprovecha para no pedirlo otra vez.
        this.store.applyUnreadCount(response.unreadCount);
        this.isLoading.set(false);
      });
  }
}
